/**
 * The draft store's client half — deletion.
 *
 * The cache contract it shares with the composer lives in
 * `hooks/mailDraftCache.js`, which is a leaf module so that
 * `hooks/useComposerDraft` can invalidate without importing `AuthContext`.
 * Re-exported here so a caller that needs both has one import.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../context/AuthContext';
import { apiDelete } from '../lib/api';
import { invalidateDrafts } from './mailDraftCache';

export { invalidateDrafts };

/**
 * Delete drafts by id — the endpoint the list's Delete should always have used.
 *
 * A draft is NOT a `communications` row, so neither lane's delete could touch
 * one: the personal lane's `POST /mail/move` matched nothing and answered a
 * cheerful `{matched: 0, modified: 0}` with a 200, and the unified lane's
 * `DELETE /inboxes/{id}/messages/{id}` answered 404. See `draftRows.js`.
 *
 * One request per id, because the API takes one id and a draft selection is
 * small by construction — `MAX_DRAFTS_PER_USER` is 200 and a page is 40.
 * `Promise.allSettled` so one failure does not discard the other results: the
 * caller needs the real split, since a partial success reported as total
 * failure is what makes a user press Delete twice.
 *
 * **A 404 counts as gone.** The draft store is per-author, so a draft the
 * server cannot find is a draft the user cannot have; failing the call for it
 * would leave a row on screen that exists nowhere.
 */
export function useDeleteDrafts(inboxId = null) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (draftIds) => {
            const ids = (draftIds || []).filter(Boolean);
            const query = inboxId
                ? `?${new URLSearchParams({ inbox_id: inboxId }).toString()}`
                : '';
            const results = await Promise.allSettled(
                ids.map((id) =>
                    apiDelete(
                        `/mail/drafts/${encodeURIComponent(id)}${query}`,
                        getAuthHeader()
                    )
                )
            );
            const failed = results.filter(
                (r) =>
                    r.status === 'rejected' && r.reason?.response?.status !== 404
            );
            return { deleted: ids.length - failed.length, failed: failed.length };
        },
        onSuccess: () => invalidateDrafts(queryClient, inboxId),
    });
}

export default useDeleteDrafts;
